import React, { useMemo } from "react"
import Image from "next/image"
import Link from "next/link"

import { Identity, OMReference, OMUniverse } from "@apptivity-lab/firmament-node-sdk"
import { CatCategoryMiniView, ConcreteCategorizable } from "@firmament-packages"
import "@firmament-packages/categories/extensions/CategorizableExtensions"
import { CnxBooking, CnxMeeting } from "@firmament-packages/connectx"
import "@firmament-packages/connectx/extensions/CnxMeetingExtensions"

import { AffAttendeeProfile, AffExpertProfile } from "@firmament-packages/aff"

import { AffExpertProfileMiniView } from "../AffExpertProfile"
import { startOfWeek, useProfile } from "@lib"
import DropdownMenu from "@components/DropdownMenu"

const CnxMeetingBookingActions: React.FunctionComponent<{
    meeting: CnxMeeting,
    attendee?: AffAttendeeProfile,
    attendeeConfirmedBookings?: CnxBooking[],
    onBookButtonClick?: (meeting: CnxMeeting) => void,
    onCancelButtonClick?: (meeting: CnxMeeting, booking: CnxBooking) => void,
    onEnterButtonClick?: (meeting: CnxMeeting) => void
}> = ({
    meeting,
    attendee,
    attendeeConfirmedBookings,
    onBookButtonClick,
    onCancelButtonClick,
    onEnterButtonClick
}) => {
    const email = useMemo(() => {
        const identities = (attendee?.user.actualObject?.identities || [])
            .reduce((result: Identity[], current: OMReference<Identity>) => {
                const identityObject = current.actualObject
                if (identityObject) {
                    result.push(identityObject)
                }
                return result
            }, [])
            .filter((each) => each.identityType === "email")

        return identities.length > 0 ? identities[0].identifier : null
    }, [attendee])

    return (
        <div className="booking-actions relative flex flex-col w-full gap-x-[12px] gap-y-[8px] items-center">
            {attendeeConfirmedBookings && (attendeeConfirmedBookings || []).length > 0 &&
                <>
                    {meeting.hasEnded()
                        ? (
                            <button type="button" className="w-full button-meeting-booking"
                                disabled={true}>
                                Ended
                            </button>
                        )
                        : (
                            <>
                                {!meeting.hasEnded() && !meeting.isLiveNow() && <DropdownMenu className="hidden sm:block" title={"BOOKED"}>
                                    <div className="truncate text-center text-alert-900 cursor-pointer"
                                        onClick={() => onCancelButtonClick && onCancelButtonClick(meeting, attendeeConfirmedBookings[0])}>
                                        <i className="fa-solid fa-ban mr-[8px]"></i>
                                        Cancel Booking
                                    </div>
                                </DropdownMenu>}

                                <form id="join-live-class-form" onSubmit={(event) => {
                                    event.preventDefault()
                                    onEnterButtonClick && onEnterButtonClick(meeting)
                                }}>
                                    {/* HubSpot only captures form submission for forms that have email field */}
                                    {email && <input type="email" hidden={true} value={email}></input>}
                                    <button type="submit" className={["mt-[12px] w-full", meeting.isLiveNow() ? "button-enter-meeting-live" : "button-enter-meeting"].join(" ")}
                                        disabled={meeting.hasEnded()}>
                                        Join Class
                                    </button>
                                </form>
                                {meeting.isLiveNow() && <p className="min-w-[90px] text-footnote text-center text-accent-900 uppercase">{"◉ Live Now"}</p>}
                                {!meeting.hasEnded() && !meeting.isLiveNow() && <p className="text-footnote text-black-600">{`on ${meeting.joinAtTimeDisplay(attendee?.timezone || undefined)}`}</p>}
                            </>
                        )
                    }
                </>
            }

            {(attendeeConfirmedBookings || []).length === 0 &&
                <form id="book-live-class-form" onSubmit={(event) => {
                    event.preventDefault()
                    onBookButtonClick && onBookButtonClick(meeting)
                }}>
                    {/* HubSpot only captures form submission for forms that have email field */}
                    {email && <input type="email" hidden={true} value={email}></input>}
                    <button type="submit" className="w-full button-meeting-booking"
                        disabled={meeting.hasEnded() || meeting.consumerCount >= meeting.maxBookings}>
                        {meeting.hasEnded() && <>Ended</>}

                        {!meeting.hasEnded() && OMUniverse.shared.currentUser?.isAuthenticated !== true && <>{meeting.consumerCount >= meeting.maxBookings ? "Sold Out" : "Sign Up"}</>}
                        {!meeting.hasEnded() && OMUniverse.shared.currentUser?.isAuthenticated === true && <>{meeting.consumerCount >= meeting.maxBookings ? "Sold Out" : "Book Now"}</>}
                    </button>
                    {meeting.isLiveNow() && <p className="min-w-[90px] text-footnote text-center text-accent-900 uppercase">{"◉ Live Now"}</p>}
                    {!meeting.hasEnded() && !meeting.isLiveNow() && meeting.consumerCount < meeting.maxBookings && <p className="text-footnote text-center text-black-600 uppercase">Available</p>}
                </form>
            }
        </div>
    )
}

const CnxMeetingMediumListCell: React.FunctionComponent<{
    meeting: CnxMeeting,
    attendee?: AffAttendeeProfile,
    attendeeConfirmedBookings?: CnxBooking[],
    expert?: AffExpertProfile,
    onClick?: (meeting: CnxMeeting) => void,
    onBookButtonClick?: (meeting: CnxMeeting) => void,
    onCancelButtonClick?: (meeting: CnxMeeting, booking: CnxBooking) => void,
    onEnterButtonClick?: (meeting: CnxMeeting) => void
}> = ({
    meeting,
    attendee,
    attendeeConfirmedBookings,
    expert,
    onClick,
    onBookButtonClick,
    onCancelButtonClick,
    onEnterButtonClick
}) => {
    const coverImageUrl = meeting.coverPhoto?.actualObject?.preview || meeting.coverPhoto?.actualObject?.url ||
        meeting.meetingTemplate?.actualObject?.coverPhoto?.actualObject?.preview || meeting.meetingTemplate?.actualObject?.coverPhoto?.actualObject?.url

    const meetingTypeCategory = useMemo(() => {
        return new OMReference(ConcreteCategorizable, meeting.id).actualObject?.meetingTypeCategory()
    }, [meeting])

    const detailPath = useMemo(() => {
        return `/classes/${meeting.id}?return=${encodeURIComponent(`from=${startOfWeek(meeting.scheduledStartAt || undefined).toISOString()}`)}`
    }, [meeting])

    return (
        <div>
            <div className="grid grid-cols-12 p-[20px] gap-x-[16px] bg-white-900">
                <div className="col-span-12 sm:col-span-1 flex text-headline text-black-900">
                    <div className="flex-1">
                        {meeting.scheduledStartAt?.toLocaleTimeString("en-US", { hour: "numeric", hour12: true, minute: "2-digit", timeZone: attendee?.timezone || undefined })}
                    </div>
                    {/* AffExpertProfileView is positioned along same row as meeting time in xs size class only. */}
                    {expert && <AffExpertProfileMiniView className="sm:hidden" expertProfile={expert} />}
                </div>

                <Link href={detailPath} passHref>
                    <div className="relative col-span-12 mt-[8px] sm:mt-0 sm:col-span-3 aspect-video cursor-pointer">
                        {coverImageUrl && <Image src={coverImageUrl} alt={meeting.title} layout="fill" objectFit="cover" />}
                    </div>
                </Link>

                <div className="col-span-12 sm:col-span-5 mt-[12px] sm:mt-0">
                    <h5 className="text-body text-black-900">{meeting.title}</h5>

                    <Link href={detailPath} passHref>
                        <div className="flex mt-[10px] gap-x-[24px]">
                            {meetingTypeCategory && <CatCategoryMiniView category={meetingTypeCategory} />}

                            <div className="flex items-center justify-items-center gap-x-[6px]">
                                <Image width="20" height="20" src="/images/ic_info_duration_tertiary.svg" alt="Meeting Duration" />
                                <h6 className="text-footnote text-black-700">{`${meeting.scheduledDurationSeconds() / 60} mins`}</h6>
                            </div>
                        </div>
                    </Link>

                    <div className="flex justify-between mt-[16px]">
                        {/* AffExpertProfileView is not positioned here in xs size class. */}
                        {expert && <AffExpertProfileMiniView className="hidden sm:block flex-1" expertProfile={expert} />}

                        <div>
                            <Link href={detailPath}>
                                <a className="flex-1 sm:flex-none my-auto text-footnote text-primary-900 uppercase underline">Details</a>
                            </Link>

                            {/* Only visible in xs size class. */}
                            {attendeeConfirmedBookings && attendeeConfirmedBookings.length > 0 &&
                                <DropdownMenu title={"BOOKED"} className="sm:hidden mt-[24px]" anchor="START">
                                    <div className="truncate text-center text-alert-900 cursor-pointer"
                                        onClick={() => onCancelButtonClick && onCancelButtonClick(meeting, attendeeConfirmedBookings[0])}>
                                        <i className="fa-solid fa-ban mr-[8px]"></i>
                                        Cancel Booking
                                    </div>
                                </DropdownMenu>
                            }
                        </div>

                        {/* Only visible in xs size class. */}
                        <div className="sm:hidden">
                            <CnxMeetingBookingActions
                                meeting={meeting}
                                attendee={attendee}
                                attendeeConfirmedBookings={attendeeConfirmedBookings}
                                onBookButtonClick={onBookButtonClick}
                                onCancelButtonClick={onCancelButtonClick}
                                onEnterButtonClick={onEnterButtonClick}
                                />
                        </div>
                    </div>
                </div>

                {/* Only visible in sm and above size classes. */}
                <div className="hidden sm:flex items-center col-span-3 px-[20px] sm:border-l-[1px]">
                    <CnxMeetingBookingActions
                        meeting={meeting}
                        attendee={attendee}
                        attendeeConfirmedBookings={attendeeConfirmedBookings}
                        onBookButtonClick={onBookButtonClick}
                        onCancelButtonClick={onCancelButtonClick}
                        onEnterButtonClick={onEnterButtonClick}
                        />
                </div>
            </div>

            <hr className="px-[20px] border-black-100" />
        </div>
    )
}

const CnxMeetingSmallListCell: React.FunctionComponent<{
    meeting: CnxMeeting,
    attendee?: AffAttendeeProfile,
    attendeeConfirmedBookings?: CnxBooking[],
    expert?: AffExpertProfile,
    onClick?: (meeting: CnxMeeting) => void,
    onBookButtonClick?: (meeting: CnxMeeting) => void,
    onCancelButtonClick?: (meeting: CnxMeeting, booking: CnxBooking) => void
    onEnterButtonClick?: (meeting: CnxMeeting) => void
}> = ({
    meeting,
    attendee,
    attendeeConfirmedBookings,
    expert,
    onClick,
    onBookButtonClick,
    onCancelButtonClick,
    onEnterButtonClick
}) => {
    const email = useMemo(() => {
        const identities = (attendee?.user.actualObject?.identities || [])
            .reduce((result: Identity[], current: OMReference<Identity>) => {
                const identityObject = current.actualObject
                if (identityObject) {
                    result.push(identityObject)
                }
                return result
            }, [])
            .filter((each) => each.identityType === "email")

        return identities.length > 0 ? identities[0].identifier : null
    }, [attendee])

    return (
        <div className={meeting.hasEnded() ? "px-[20px] bg-black-50" : "px-[20px] bg-white-900"}>
            <hr className="bg-black-100" />

            <div className="grid grid-cols-12 py-[16px]">
                {/* Not visible in xs size class */}
                <div className={["hidden sm:block col-span-2 text-headline"].concat([meeting.hasEnded() ? "text-black-400" : "text-black-900"]).join(" ")}
                     onClick={() => onClick && onClick(meeting)}>
                    <div>{meeting.scheduledStartAt!.toLocaleDateString("en-US", { weekday: "short", timeZone: attendee?.timezone || undefined })},</div>
                    <div>{meeting.scheduledStartAt!.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: attendee?.timezone || undefined })}</div>
                </div>

                <div className="flex flex-col col-span-8 sm:col-span-7 mt-[8px] sm:mt-0 gap-y-[8px]">
                    {/* Only visibile in xs size class */}
                    <div className={["sm:hidden text-headline text-black-900"].concat([meeting.hasEnded() ? "text-black-400" : "text-black-900"]).join(" ")}>
                        {meeting.scheduledStartAt!.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: attendee?.timezone || undefined })}
                    </div>

                    <div className={["text-body"].concat([meeting.hasEnded() ? "text-black-400" : "text-black-900"]).join(" ")}>
                        {meeting.scheduledStartAt!.toLocaleTimeString("en-US", { hour: "numeric", hour12: true, minute: "2-digit", timeZone: attendee?.timezone || undefined })} - {meeting.scheduledEndAt!.toLocaleTimeString("en-US", { hour: "numeric", hour12: true, minute: "2-digit", timeZone: attendee?.timezone || undefined })}
                    </div>

                    {!meeting.hasEnded() && meeting.consumerCount === 0 &&
                        <div className={["text-footnote text-black-700"].join(" ")}>
                            Be the first one to book!
                        </div>
                    }
                </div>

                <div className="col-span-4 sm:col-span-3 flex items-center justify-items-center">
                    <div className="sm-booking-actions w-full relative flex flex-col items-center">
                        {attendeeConfirmedBookings && (attendeeConfirmedBookings || []).length > 0 &&
                            <>
                                {meeting.hasEnded()
                                    ? (
                                        <button type="button" className="w-full button-meeting-booking"
                                            disabled={true}>
                                            Ended
                                        </button>
                                    )
                                    : (
                                        <>
                                            {!meeting.hasEnded() && !meeting.isLiveNow() && <DropdownMenu title={"BOOKED"} className="mt-[8px]">
                                                <div className="truncate text-center text-alert-900 cursor-pointer"
                                                    onClick={() => onCancelButtonClick && onCancelButtonClick(meeting, attendeeConfirmedBookings[0])}>
                                                    <i className="fa-solid fa-ban mr-[8px]"></i>
                                                    Cancel Booking
                                                </div>
                                            </DropdownMenu>}

                                            <form id="join-live-class-form" onSubmit={(event) => {
                                                event.preventDefault()
                                                onEnterButtonClick && onEnterButtonClick(meeting)
                                            }}>
                                                {/* HubSpot only captures form submission for forms that have email field */}
                                                {email && <input type="email" hidden={true} value={email}></input>}
                                                <button type="submit" className={["mt-[16px] w-full", meeting.isLiveNow() ? "button-enter-meeting-live" : "button-enter-meeting"].join(" ")}>
                                                    Join Class
                                                </button>
                                            </form>
                                            {meeting.isLiveNow() && <p className="mt-[4px] text-footnote text-center text-accent-900 uppercase">{"◉ Live Now"}</p>}
                                            {!meeting.hasEnded() && !meeting.isLiveNow() && <p className="mt-[4px] text-footnote text-black-600">{`on ${meeting.joinAtTimeDisplay(attendee?.timezone || undefined)}`}</p>}
                                        </>
                                    )
                                }
                            </>
                        }
                        {(attendeeConfirmedBookings || []).length === 0 &&
                            <form id="book-live-class-form" onSubmit={(event) => {
                                event.preventDefault()
                                onBookButtonClick && onBookButtonClick(meeting)
                            }}>
                                {/* HubSpot only captures form submission for forms that have email field */}
                                {email && <input type="email" hidden={true} value={email}></input>}
                                <button type="submit" className="mt-[8px] w-full button-meeting-booking" disabled={meeting.hasEnded() || meeting.consumerCount >= meeting.maxBookings}>
                                    {meeting.hasEnded() && <>Ended</>}

                                    {!meeting.hasEnded() && OMUniverse.shared.currentUser?.isAuthenticated !== true && <>{meeting.consumerCount >= meeting.maxBookings ? "Sold Out" : "Sign Up"}</>}
                                    {!meeting.hasEnded() && OMUniverse.shared.currentUser?.isAuthenticated === true && <>{meeting.consumerCount >= meeting.maxBookings ? "Sold Out" : "Book Now"}</>}
                                </button>
                                {meeting.isLiveNow() && <p className="mt-[8px] text-footnote text-center text-accent-900 uppercase">{"◉ Live Now"}</p>}
                                {!meeting.hasEnded() && !meeting.isLiveNow() && meeting.consumerCount < meeting.maxBookings && <p className="mt-[8px] text-footnote text-center text-black-600 uppercase">Available</p>}
                            </form>
                        }
                    </div>
                </div>
            </div>
        </div>
    )
}

const CnxMeetingCell: React.FunctionComponent<{
    variant: "SMALL_LIST" | "MEDIUM_LIST",
    meeting: CnxMeeting,
    onClick?: (meeting: CnxMeeting) => void,
    onBookButtonClick?: (meeting: CnxMeeting) => void,
    onCancelButtonClick?: (meeting: CnxMeeting, booking: CnxBooking) => void,
    onEnterButtonClick?: (meeting: CnxMeeting) => void
}> = ({ variant, meeting, onClick, onBookButtonClick, onCancelButtonClick, onEnterButtonClick }) => {
    const attendeeProfile = useProfile(AffAttendeeProfile)

    const expertProfile = useMemo(() => {
        const provider = meeting.provider
        if (!provider) {
            return undefined
        }

        return new OMReference(AffExpertProfile, provider.id).actualObject
    }, [meeting.provider])

    const meetingTypeCategory = useMemo(() => {
        return new OMReference(ConcreteCategorizable, meeting.id).actualObject?.meetingTypeCategory()
    }, [meeting])

    const confirmedAttendeeBookings = useMemo(() => {
        return (meeting.bookings || []).reduce<CnxBooking[]>((result, booking) => {
            const actualBooking = booking.actualObject
            if (actualBooking && actualBooking.bookingStatus === "CONFIRMED" &&
                actualBooking?.consumer?.id === attendeeProfile?.id) {
                result.push(actualBooking)
            }
            return result
        }, [])
    }, [attendeeProfile, meeting.bookings])

    switch (variant) {
        case "SMALL_LIST":
            return <CnxMeetingSmallListCell
                meeting={meeting}
                attendee={attendeeProfile}
                attendeeConfirmedBookings={confirmedAttendeeBookings}
                expert={expertProfile}
                onClick={onClick}
                onBookButtonClick={onBookButtonClick}
                onCancelButtonClick={onCancelButtonClick}
                onEnterButtonClick={onEnterButtonClick}
                />
        case "MEDIUM_LIST":
            return <CnxMeetingMediumListCell
                meeting={meeting}
                attendee={attendeeProfile}
                attendeeConfirmedBookings={confirmedAttendeeBookings}
                expert={expertProfile}
                onClick={onClick}
                onBookButtonClick={onBookButtonClick}
                onCancelButtonClick={onCancelButtonClick}
                onEnterButtonClick={onEnterButtonClick}
                />
    }
}

export default CnxMeetingCell
